import api from './api';
import { cachedGet } from '../utils/lookupCache';
import type { LegSnapshotItem } from '../utils/legSavePayload';
import type { OrderDataRow } from '../utils/buildOrderDataRows';
import type { TroubleTicketEntry } from '../components/TroubleTicketBadge';

export interface Order {
  plannerDropReleased?: boolean;
  dropReleasedNotDispatched?: boolean;
  pronumber: number;
  moveType?: string;
  legnum?: number;
  billToName: string;
  customerName: string;
  keyAccount: string;
  customerRecno?: number;
  dispatcher?: string;
  region?: string;
  appointmentDate: string;
  originAppointmentDate: string;
  originName: string;
  /** Full location name (lname). originName shows the alias when set; this
   * carries the full name for the hover tooltip + global search. */
  originLname?: string;
  originCityState: string;
  originCoordinates: string;
  origcity?: string;
  origstate?: string;
  origzip?: string;
  origcode?: number;
  destcode?: number;
  destinationAppointmentDate: string;
  destinationName: string;
  /** Full location name (lname); destinationName shows the alias when set. */
  destinationLname?: string;
  destinationCityState: string;
  destinationCoordinates: string;
  destcity?: string;
  deststate?: string;
  destzip?: string;
  driver: string;
  container: string;
  bol: string;
  size: string;
  lbs: number | null;
  equipType: string;
  chassisId: string;
  chassisType: string;
  chassisProvider: string;
  scac: string;
  offhire: string;
  redeliveryExpirationDate?: string;
  cutoffDatetime: string;
  documentCutoffDatetime: string;
  lfdDate: string;
  custTargetDate: string;
  perDiemDate: string;
  firstRcvDate: string;
  legType: string;
  hasPickup: boolean;
  hasDelivery: boolean;
  hasTerminate: boolean;
  isMultiStop: boolean;
  legs: OrderLeg[];
  hot: string;
  isDaycab: string;
  hazmat: string;
  isTank: string;
  overweightPermitRequired: string;
  requiresPpe?: string;
  otr: string;
  rescheduleCount: number;
  rescheduleHistory?: { reason: string; old_date: string; new_date: string; created: string }[];
  // Open (NEW/PENDING) trouble tickets for the Dispatch Planner's "Open TT"
  // column, and for the open-trouble-ticket dispatch warning. Attached by the
  // backend's attach_open_trouble_tickets on the imports/exports/dropreleased
  // listings and on available-pros-all (Smart Dispatch) — absent elsewhere,
  // hence optional. Resolved and cancelled tickets are excluded server-side.
  openTtCount?: number;
  openTickets?: TroubleTicketEntry[];
  // Open repair orders reaching this order by pronumber, container or chassis.
  // Attached by attach_open_repair_orders on the same listings as openTtCount.
  openRoCount?: number;
  origIsNewLocation?: string;
  destIsNewLocation?: string;
  isDrop?: string;
  deliveryLeg?: number;
  dropReleaseDatetime?: string;
  streetTurnOpportunity?: string;
  // When this dispatch-group row went onto the board (the LC finalize stamp from
  // scheduling_history), naive ISO. Attached by attach_time_on_board on the
  // imports/exports/dropreleased listings; null for legs scheduled outside the
  // LC flow. Drives the Dispatch Planner's "Time on Board" column.
  boardSince?: string | null;
  isEarly: boolean;
  isLiveLoad: boolean;
  destApptTime1?: string;
  destApptTime2?: string;
  dispatchScopeLegs?: DispatchScopeLeg[];
  warnings?: { code: string; message: string }[];
}

// Leg info for dispatch scope (used by Smart Dispatch to show grouped legs)
export interface DispatchScopeLeg {
  legtype?: string;
  legAction2?: string;
  isTerminate?: boolean;
  legnum: number;
  origcode: number;
  destcode: number;
  legDrop: string;
  driver: string;
  origApptDate: string;
  destApptDate: string;
  legMiles: number | string;
  originName: string;
  originCityState: string;
  originCoordinates: string;
  destName: string;
  destCityState: string;
  destCoordinates: string;
}

export interface OrderLeg {
  isMule?: boolean;
  legrecno: number;
  legnum: number;
  legtype: string;
  origcoordinates: string;
  origcode: number;
  origApptDate: string;
  origApptTime1?: string;
  origApptTime2?: string;
  origApptNumber?: string;
  origArrivalDateTime?: string | null;
  origDepartDateTime?: string | null;
  origName: string;
  /** Full location name (lname); origName shows the alias when set. */
  origLname?: string;
  origCityState: string;
  origcity?: string;
  origstate?: string;
  origzip?: string;
  legAction2: string;
  destcoordinates: string;
  destcode: number;
  destApptDate: string;
  destApptTime1?: string;
  destApptTime2?: string;
  destApptNumber?: string;
  destArrivalDateTime?: string | null;
  destDepartDateTime?: string | null;
  destName: string;
  /** Full location name (lname); destName shows the alias when set. */
  destLname?: string;
  destCityState: string;
  destcity?: string;
  deststate?: string;
  destzip?: string;
  legDrop: string;
  driver: string;
  legMiles?: number;
  origmemo?: string;
  legmemo?: string;
  legstatus?: string;
  dispatchid?: number;
  plannedWithSmartPreplan?: boolean;
  donotpayleg?: string;
  dspdriverdatetime?: string;
  origgeoarrivaldatetime?: string;
  origgeodepartdatetime?: string;
  destgeoarrivaldatetime?: string;
  destgeodepartdatetime?: string;
  origapptrequesteddatetime?: string;
  destapptrequesteddatetime?: string;
  origrequireinspection?: string;
  origrequireinterchange?: string;
  origrequirebol?: string;
  destrequirepod?: string;
  destrequireinterchange?: string;
  destrequirescaleticket?: string;
  originspectionrecno?: number;
  originterchangerecno?: number;
  origbolrecno?: number;
  destpodrecno?: number;
  destinterchangerecno?: number;
  destscaleticketrecno?: number;
  loadedLeg: number;
  dispatchStatus?: 'success' | 'error' | 'pending';
  errorReason?: string;
  sendToGwiMove?: boolean;
  isPaid?: boolean;
  acceptdatetime?: string;
  driverPayRate?: number | null;
  driverPayFuelType?: string;
  driverPayStatus?: string;
  driverPayDate?: string;
  driverPayApprovedBy?: string;
  driverPayApprovedDatetime?: string;
  /** driverpay_lineitem.recno for this leg's pay line — drives the "Remove Pay
   *  and Open Leg" (Clear Leg) action in the dispatch-info modal. 0 when none. */
  driverPayRecno?: number;
  /** The driver ON the non-deleted pay line (v1 dpay->driver) — empty until a pay
   *  line has been generated. Distinct from `driver` (the leg's ASSIGNED driver):
   *  the dispatch-info modal splits pay-rows vs "Do Not Pay?" on THIS, matching v1
   *  driverstatusinfo, so "Do Not Pay?" stays visible on an assigned-but-unpaid leg. */
  driverPayDriver?: string;
  /** True while the pay line is not yet invoiced (payrolldate set + newer than
   *  the latest driverpay_invoice run) — v1 `allowedit`. Gates Clear Leg. */
  driverPayAllowEdit?: boolean;
  /** True when the order's dispatcher is a GWI dispatcher — v1 gates the whole
   *  Driver Pay Information block (and Clear Leg) on this. */
  orderIsGwiDispatcher?: boolean;
  /** True when the leg's assigned unit/driver is a gwiMove driver
   *  (safetynew.unit.usegwimove) — independent of whether THIS leg was sent to
   *  gwimove. v1 gwiDriver[driver]; widens the Payment Requirements section's
   *  visibility (sendToGwiMove OR (gwiMove-driver AND not-invoiced)). */
  driverUsesGwiMove?: boolean;
  requiresPpe?: string;
  missingApptDate?: boolean;
}

export interface AddlCostDriverPayItem {
  recno: number;
  type: string;
  driver: string;
  rate: number | null;
  /** #33649 — driverpay_rates row behind the rate, as STORED. Not a reliable
   *  MISSING RATE signal on its own: additions created through /pro before
   *  #33649 never persisted it. Use `effectiveRateRecno`. */
  rateRecno?: number;
  /** #33741 — stored rateRecno, else the engine's live verdict for the lane.
   *  0 on a BOBTAIL means MISSING RATE: no zone rate on file, so the line can't
   *  be approved. */
  effectiveRateRecno?: number;
  fuelType: string;
  /** Fuel surcharge on the gross rate, using the applicable historical fuel rate. */
  fuelAmount?: number;
  payStatus: string;
  eventDate: string;
  payrollDate: string;
  legnum: number | null;
  legType: string;
  legAction2: string;
  origName: string;
  destName: string;
  lineItemPro: string;
  /** 'Y' when GWI takes a flat 30% commission out of this line item's rate. */
  gwiCommission: string;
  /** True once this addition's payroll has been invoiced — the row is locked
   *  from edits and deletes (backend rejects with 409). */
  isPaid?: boolean;
}

export interface AddlCostItem {
  recno: number;
  invoiceNumber: string;
  costType: string;
  invoiceTotal: number;
  gwiPayment: number;
}

export interface RejectedDriverPaperworkItem {
  recno: number;
  createdBy: string;
  infoType: string;
  rejectedDatetime: string;
}

/** One file attached to a leg document (photo/attachment or POD signature). Streamed
 *  via getLegDocumentFileBlob(recno, fileRecno). Mirrors V1 legdocumentinfo attachments. */
export interface LegDocumentAttachment {
  fileRecno: number;
  filename: string;
  filetype: string;
  documentType: string;
  isImage: boolean;
}

/** Full detail for one legdocumentinfo record — the View side of the V1 CMS
 *  legdocumentinfo modal. Metadata fields are '' when not populated (rendered only
 *  when present). hasPdf drives the "View PDF" link (streams pdffileinforecno). */
export interface LegDocumentDetail {
  recno: number;
  pronumber: number;
  doctype: string;
  doctypeLabel: string;
  infotype: string;
  createdBy: string;
  createdDatetime: string;
  safeToMove: string;
  container: string;
  chassisId: string;
  pieceCount: string;
  lbs: string;
  dataMatch: string;
  dataComments: string;
  sealNumber: string;
  sealMatch: string;
  podSignedBy: string;
  podEmailAddress: string;
  missingSignatureReason: string;
  comments: string;
  rejectedBy: string;
  rejectedDatetime: string;
  rejectedReason: string;
  rejectedComments: string;
  hasPdf: boolean;
  pdfFileRecno: number;
  attachments: LegDocumentAttachment[];
}

export interface GroundedInfoItem {
  recno: number;
  /** cms.locations recno for the grounded location (0 when unresolved). */
  locRecno: number;
  locName: string;
  startDate: string;
  endDate: string;
  /** Days between startDate and endDate inclusive, or null when endDate is open. */
  days: number | null;
}

/** Payload for adding a grounded-dates row. Dates are YYYY-MM-DD; endDate blank = open. */
export interface GroundedDateCreate {
  locrecno: number;
  locName: string;
  startDate: string;
  endDate?: string;
}

/** Partial update — send only changed fields. endDate:'' clears (reopens) the period. */
export interface GroundedDateUpdate {
  locrecno?: number;
  locName?: string;
  startDate?: string;
  endDate?: string;
}

export interface HazmatInfoItem {
  recno: number;
  hazmatUn: string;
  shippingName: string;
  hazmatClass: string;
  hazmatPkg: string;
  limitedQty: string;
  /** Line is NOT matched by a cms.hazmat_exceptions row → needs approval
   *  (orange highlight). */
  needApproval?: boolean;
  /** Explosive (1.x) / radioactive (7.x) class we don't haul, other than the
   *  1.4 division → approval disabled + red highlight. */
  donotHaul?: boolean;
}

/** One hazmat reference line from cms.hazmatdata (the UN lookup result). */
export interface HazmatLookupItem {
  hazmatUn: string;
  shippingName: string;
  hazmatClass: string;
  hazmatPkg: string;
}

/** Pro-level hazmat approval state. V1 stores it on
 *  ordersall.hazmatapprovedby (presence = approved) and records each
 *  toggle in cms.history. */
export interface HazmatApproval {
  approved: 'Y' | 'N';
  /** Current approver (the value of ordersall.hazmatapprovedby). */
  approvedBy: string;
  /** Timestamp of the latest cms.history row for this column. */
  approvedDatetime: string;
  /** The user who performed the most recent toggle (may differ from approvedBy
   *  when an approval is cleared). */
  toggleUserId: string;
  /** Any non-exempt hazmat line exists → show the approval checkbox (V1 only
   *  renders it when a line needs approval). */
  needApproval?: boolean;
  /** Any do-not-haul line exists → the approval checkbox is disabled (V1
   *  getHazmatInfo). */
  donotHaul?: boolean;
}

export interface InvoiceMain {
  recno: number;
  /** `invoice.invhistoryrecno` — the raised invoice this charge was billed under, and
   *  what the Invoice Options modal is addressed by. 0 on a draft and on a template. */
  invHistoryRecno?: number;
  /** The INVOICE's bill-to, which is the one Invoice Options reads recipients and
   *  grouping from. Not necessarily the order's — a charge can be billed elsewhere. */
  billTo?: number;
  invoiceDate: string;
  masExportDate: string;
  billFromSystem: string;
  doNotBillReason: string;
  creditAmount: number;
  creditReason: string;
  invoiceTotal: number;
  invoiceComments: string;
  quoteNumber: number;
  revisionNumber: number;
  /** Display-only fallback revision for a TEMPLATE that pins a quote number but
   *  no revision (the revision is chosen when an order is created from it). The
   *  server sends the quote's CURRENT revision — masterquote.currentrevisionnumber,
   *  which does not advance to a future-dated revision until its effective date
   *  arrives — so this never previews rates that are not live yet. 0 on a real
   *  order and on a template that already pins one. Never staged, never saved. */
  quoteCurrentRevision?: number;
  // Legacy v2 quote cross-reference (display-only; no live V2 system to link to).
  v2QuoteNumber: number;
  v2RevisionNumber: number;
  v2Lane: string;
  requireEdi: string;
  requireMrpl: string;
  requireInInterchange: string;
  requirePod: string;
  requireBol: string;
  requireOutInterchange: string;
  requireChassisInEir: string;
  requireChassisOutEir: string;
  requireRevisedDo: string;
  pendingApproval: string;
  estInvoiceEmail: string;
  billToRefNo: string;
  /** Customer-facing invoice number (invoice_history.masterpro). Blank until the
   *  invoice is actually invoiced — pros billed together share one number. */
  invoiceNumber: string;
  rebillCount?: number;
  accessorialCount?: number;
}

/**
 * Editable invoice-header fields persisted by POST /orders/{pro}/invoice-header.
 * The backend splits these across ordersall + invoice tables. Every field is
 * optional — only the ones present (dirty) are sent. Field names match the
 * invoiceMain read keys. NOTE: the invoice-approval stamp (approvedby) is NOT
 * here — it rides the main /save-order-details orderData path so sr_approved
 * fires. This patch is committed by the same footer Save, right after the order.
 */
export interface InvoiceHeaderPatch {
  // INVOICE-TABLE fields only. The ordersall invoice-tab fields (cod, freightTerms,
  // requirePerDiemApproval, marginApprovedBy/Datetime, rateComments, rebill,
  // invoiceHoldReason/Followup) ride the orderData path in BOTH modes — including
  // them here too would double-write ordersall in cmsapi mode.
  invoiceComments?: string;
  doNotBillReason?: string;
  creditAmount?: number | null;
  /** Why a credit was issued (V1 CREDIT_REASONS). Editable on the main invoice's
   *  Invoicing tab whenever creditamt is non-zero; creditamt itself is read-only
   *  there, so this is the writable half of the pair. */
  creditReason?: string;
  billToRefNo?: string;
  estInvoiceEmail?: string;
  // billfromsystem (CMS / DO NOT BILL) — editable on a real order for anyone
  // holding order-lc-fields Edit, and on an UNAPPROVED template (State 2).
  // Routes to invoice / templateinvoice respectively.
  billFromSystem?: string;
  requirePod?: string;
  requireBol?: string;
  requireMrpl?: string;
  requireEdi?: string;
  requireInInterchange?: string;
  requireOutInterchange?: string;
  requireChassisInEir?: string;
  requireChassisOutEir?: string;
  requireRevisedDo?: string;
  pendingApproval?: string;
  /** Linked quote (V1 "Quote # / Rev #"). Always sent as a pair; 0/0 clears it. */
  quoteNumber?: number;
  revisionNumber?: number;
}

/**
 * editedOrderFields keys that persist through POST /invoice-header (NOT the
 * main orderData path). Kept in sync with InvoiceHeaderPatch. NOTE: this list
 * deliberately excludes `invoiceApprovedBy`/`invoiceApprovedDatetime` (they ride
 * orderData so sr_approved fires) and `billToContactRecno` (an ordersall column
 * on the orderData map). `creditAmount` arrives as a string from the input and
 * is coerced below.
 */
// INVOICE-TABLE fields only. The ordersall invoice-tab fields (cod, freightTerms,
// requirePerDiemApproval, marginApprovedBy/Datetime, rateComments, rebill,
// invoiceHoldReason/Followup) ride the ORDER section (orderData via
// ORDER_FIELD_COLUMNS) in both modes, so they're NOT in this list.
export const INVOICE_HEADER_FIELD_KEYS = [
  'invoiceComments', 'doNotBillReason', 'creditAmount', 'creditReason',
  'billToRefNo', 'estInvoiceEmail',
  'billFromSystem',
  'requirePod', 'requireBol', 'requireMrpl', 'requireEdi', 'requireInInterchange',
  'requireOutInterchange', 'requireChassisInEir', 'requireChassisOutEir',
  'requireRevisedDo', 'pendingApproval',
  // Linked quote (the Quote # picker). Both halves are always staged together, so
  // the pair travels intact through whichever save mode is active.
  'quoteNumber', 'revisionNumber',
] as const;

// editedOrderFields key -> invoice-table DB column, for the native engine's
// invoiceData section. Same field set as INVOICE_HEADER_FIELD_KEYS (the
// side-channel patch), just mapped to columns.
const INVOICE_DATA_COLUMNS: Record<string, string> = {
  invoiceComments: 'invoicecomments',
  doNotBillReason: 'donotbillreason',
  creditAmount: 'creditamt',
  creditReason: 'creditreason',
  billToRefNo: 'billtorefno',
  estInvoiceEmail: 'estinvoiceemail',
  billFromSystem: 'billfromsystem',
  requirePod: 'requiredpod',
  requireBol: 'requiredbol',
  requireMrpl: 'requiredmrpl',
  requireEdi: 'requirededi',
  requireInInterchange: 'requiredininterchange',
  requireOutInterchange: 'requiredoutinterchange',
  requireChassisInEir: 'requiredchassisineir',
  requireChassisOutEir: 'requiredchassisouteir',
  requireRevisedDo: 'requiredreviseddo',
  pendingApproval: 'pendingapproval',
  // These two also fire the engine's order_quoterevision rule (secure the
  // revision, sync CSRs to the master quote, set marginlimitlocal) — V1's
  // sr_quoterevision behavior on a quote attach.
  quoteNumber: 'quotenumber',
  revisionNumber: 'revisionnumber',
};

/**
 * Credit Amt as the API wants it. The input is a free-text box filtered to digits and
 * one decimal point, so it can legitimately hold "" (cleared) or a lone "." mid-typing.
 *
 * Both map to 0, NOT null: `creditamt` is `DECIMAL NOT NULL DEFAULT 0.00` and every
 * writer skips the field when it arrives as null (`if payload.creditAmount is not
 * None`), so sending null for a cleared box silently left the old credit in place.
 * Zero is the value V1 stores and the value every reader treats as "no credit".
 */
export function creditAmountValue(raw: unknown): number {
  const parsed = parseFloat(String(raw ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Build the native engine's invoiceData rows ([{column,newValue,oldValue}]) from
 * the edited invoice-table fields, mirroring buildOrderDataRows. `creditAmount`
 * coerces to number|null. Returns undefined when nothing invoice-table changed.
 */
export function buildInvoiceDataRows(
  edited: Record<string, any>,
  current: Partial<OrderDetails>,
): Array<{ column: string; newValue: any; oldValue: any }> | undefined {
  const inv = current.invoiceMain ?? null;
  const rows: Array<{ column: string; newValue: any; oldValue: any }> = [];
  for (const key of Object.keys(INVOICE_DATA_COLUMNS)) {
    if (edited[key] === undefined) continue;
    const column = INVOICE_DATA_COLUMNS[key];
    let newValue = edited[key];
    let oldValue = (inv as Record<string, any> | null)?.[key] ?? '';
    if (key === 'creditAmount') {
      newValue = creditAmountValue(newValue);
      oldValue = inv?.creditAmount ?? 0;
    }
    rows.push({ column, newValue, oldValue });
  }
  return rows.length > 0 ? rows : undefined;
}

/**
 * Pick the invoice-header fields present in an editedOrderFields map and shape
 * them into an InvoiceHeaderPatch. Returns null when none are present (so the
 * caller can skip the POST). `creditAmount` is coerced from its string input to a
 * number by `creditAmountValue` — a cleared box becomes 0, never null.
 */
export function buildInvoiceHeaderPatch(
  edited: Record<string, any>,
): InvoiceHeaderPatch | null {
  const patch: Record<string, any> = {};
  let any = false;
  for (const key of INVOICE_HEADER_FIELD_KEYS) {
    if (edited[key] === undefined) continue;
    any = true;
    if (key === 'creditAmount') {
      patch[key] = creditAmountValue(edited[key]);
    } else {
      patch[key] = edited[key];
    }
  }
  return any ? (patch as InvoiceHeaderPatch) : null;
}

export interface InvoiceLineItem {
  recno: number;
  lineNumber: number;
  item: string;
  glCode: string;
  qty: number;
  rate: number;
  rateType: string;
  description: string;
  total: number;
}

export interface AccessorialInvoice {
  recno: number;
  accessorialNum: number;
  billTo: number;
  billToName: string;
  /** The raised invoice this accessorial was billed under — each accessorial is
   *  invoiced on its own, so this is per row. 0 until it is invoiced. */
  invHistoryRecno?: number;
  /** Customer-facing number printed on that invoice (invoice_history.masterpro). */
  invoiceNumber?: string;
  billFromSystem: string;
  doNotBillReason: string;
  invoiceDate: string;
  /** Sage export date (mm/dd). Present so the inline accessorial list seeds with
   *  the correct invoiced(red)/exported(black) highlight on first paint, before
   *  the accessorials service refines the row in. */
  masExportDate?: string;
  invoiceTotal: number;
}

export interface OrderDetails {
  mileageBand?: number | null;
  pronumber: number;
  moveType: string;
  billToName: string;
  billTo: number;
  customerName: string;
  customer?: number;
  keyAccount: string;
  lbs?: number;
  kgs?: number;
  metricTons?: number;
  commodity?: string;
  dispatcher?: string;
  dispatcherDisplayName: string;
  dispatcherRegion: string;
  dispatcherIsGwi?: boolean;
  hot: string;
  isDaycab: string;
  hazmat: string;
  isTank: string;
  otr: string;
  /** Order-level scale flag (orderscurrent.scale). 'Y' / 'N'. */
  scale?: string;
  /** Order-level swing flag (orderscurrent.swing). 'Y' / 'N'. */
  swing?: string;
  /**
   * Service Failure flag. Backed by the repurposed `dvirframe` column on
   * orderscurrent — the column was originally DVIR-related (Driver Vehicle
   * Inspection Report) but is now used to mark a service failure on the
   * order. Toggling N→Y opens the reschedule modal in OrderFormView.
   */
  serviceFailure?: string;
  orderLC?: string;
  csr?: string;
  whseCsr?: string;
  whseCsrUsername?: string;
  cstDispatcher?: string;
  carrier?: string;
  /** Carrier FK (ordersall.carriercode = highway carrier recno). Display name
   * is `carrier`; edits store this recno via the CarrierLookupField. */
  carrierCode?: number | string;
  /** Carrier's SCAC — display-only, used to derive the outside-carrier
   * pseudo-driver code. Not saved (not in ORDER_FIELD_COLUMNS). */
  carrierScac?: string;
  overweightPermitRequired: string;
  overweightApprovedBy?: string;
  /** Auto-copied server-side from the assigned OW driver's permit for the
   * delivery county (port of v1 leg_updateowpermitnum). Round-trips via
   * get + save; not shown on the page. */
  overweightPermitNumber?: string;
  overweightApprovedDatetime?: string;
  oversizePermitRequired: string;
  /** Cargo dimensions in inches (V1 ordersall.lengthinches/widthinches/heightinches).
   *  Feet/inches and cm are derived client-side for display only — never persisted. */
  lengthInches?: number;
  widthInches?: number;
  heightInches?: number;
  /** Computed out-of-gauge flag (any of oversize/overweight). User-editable Y/N
   *  select in the ChassisSpecsModal, silently overwritten by the live
   *  /oog/compute recompute (V1 parity). */
  oog?: string;
  /** Master "OOG Legal" override — when 'Y', size/weight checks short-circuit
   *  to all-clear (see app/core/oog_specs.py compute_oog). */
  ooglegal?: string;
  /** Computed broker-out flag. Same editable-but-overwritten behavior as oog. */
  brokerout?: string;
  oversizePermitNumber?: string;
  oversizeApprovedBy?: string;
  oversizeApprovedDatetime?: string;
  /** Forced Y/N off chassisType containing "TRIAXLE" (V1 parity side effect of
   *  the ChassisSpecsModal Save). */
  triaxle?: string;
  requiresPpe?: string;
  railbilled: string;
  container: string;
  size: string;
  equipType: string;
  isGwiEquip: string;
  flexBooking: string;
  chassisId: string;
  chassisType: string;
  chassisProvider: string;
  scac: string;
  lastFreeDay: string;
  examLfd?: string;
  redeliveryExpirationDate?: string;
  /** Most recent storage-cleared-through date (formatted like LFD), or '' if none. */
  storageClearedDate: string;
  /** Customer Target Date (orderscurrent.deliveryoption1date), formatted like LFD. */
  custTargetDate: string;
  /** Target delivery date (orderscurrent.targetdeliverydate), formatted like LFD. */
  targetDeliveryDate: string;
  /** Target on/by selector (orderscurrent.targetonorby): 'ON' | 'BY' | ''. */
  targetOnOrBy: string;
  perDiem: string;
  firstRcv: string;
  cutoffDatetime: string;
  isDrop?: string;
  originateLeg?: number;
  deliveryLeg?: number;
  terminateLeg?: number;
  dropReleaseDatetime: string;
  dropReleaseComment: string;
  /** Bobtail Approved flag — `bobtail_approved` char(1) 'Y' | 'N' on
   *  ordersall / orderscurrent / template. Checkbox after Drop Rel on the
   *  Order Form tab. */
  bobtailApproved?: string;
  comments?: string;
  offhire?: string;
  bookingNumber?: string;
  foodGrade?: string;
  streetTurnHold?: string;
  reuseContainerPro?: number;
  reuseImportPro?: number;
  reuseDate?: string;
  reuseBy?: string;
  reuseOrigCode?: number;
  reuseOrigLocation?: string;
  reuseStatus?: string;
  reuseStatusDatetime?: string;
  reuseStatusUser?: string;
  equipReposition?: string;
  rescheduledCount?: number;
  terminal?: string;
  /**
   * V1 "Terminal" column on orderscurrent — rail/port carrier code
   * (e.g. SAV-GCT, HOU-BCT, BNSF). Distinct from `terminal` above which
   * is sourced from `portterminalname`. Edited via the Terminal dropdown
   * on the Pro Detail Order Info tab.
   */
  rail?: string;
  /**
   * V1 `railterminal.dotrace` for this order's rail terminal (funcs.pl ~125).
   * 'Y' = traceable rail, 'N' = non-traceable rail row, '' = no rails row.
   * Drives the LFD-color gate (see isLfdColorActive) so the proximity red
   * clears once the container is out-gated / a driver is assigned, matching V1.
   */
  railTraceable?: string;
  idoNumber?: string;
  pieceCount?: string;
  purchaseOrder?: string;
  tareLbs?: number | null;
  vessel?: string;
  voyage?: string;
  portOfOrigin?: string;
  portOfDischarge?: string;
  /** cms.ports recnos (the editable FK); the *Name fields above are the joined
   *  read-only display, re-derived on refetch (same pattern as consigneeCode). */
  portOfOriginCode?: number;
  portOfDischargeCode?: number;
  sealNumber?: string;
  pregatepin?: string;
  emptypregatepin?: string;
  pregatechassis?: string;
  emptypregatechassis?: string;
  /** Last auto-pregate failure reason from cmstrace/autopregate.pl. Cleared when a
   *  PIN comes back or the pro drops out of the pregate selection, so a non-empty
   *  value means the most recent attempt failed and there is still no PIN.
   *  Read-only — written by the cron, never edited from here. */
  pregateerror?: string;
  emptypregateerror?: string;
  /** Return equipment for Domestic moves (movetype='DOMESTIC'). returnPronumber
   *  is the linked return-move PRO (0 = none); the equip/chassis are free text.
   *  Only surfaced in the Pro drawer's Order Info tab for Domestic moves. */
  returnPronumber?: number;
  returnEquipment?: string;
  returnChassis?: string;

  // Addl Info: Customer / Broker / Yard / Drop Release
  consigneeName?: string;
  /** The named consignee's location is retired in Data Management. The order still
   *  carries it — 67 open orders do — so the screen says so rather than reading as an
   *  ordinary stop. Already true of the join that fetches the name, so it costs nothing. */
  consigneeDeleted?: boolean;
  /** Same, for the customer. */
  customerDeleted?: boolean;
  consigneeCode?: number;
  brokerName?: string;
  brokerCode?: number;
  brokerContactName?: string;
  brokerContactRecno?: number;
  brokerContactPhone?: string;
  brokerContactEmail?: string;
  brokerRefNo?: string;
  shipperName?: string;
  dcNumber?: string;
  dept?: string;
  priority?: string;
  priority2?: string;
  dcDueDate?: string;
  nonDeliveryReason?: string;
  yardReportDatetime?: string;
  notOnYardReportDate?: string;
  fullEmptyDate?: string;
  yardLocation?: string;
  demurrageReason?: string;
  packaged?: string;
  equipHold?: string;
  allowBrokerage?: string;
  streetTurnReview?: string;

  // Addl Info: Customs / Inbond / Whse / Temp / Cargo
  isExam?: string;
  examiner?: string;
  examNo?: string;
  examStatus?: string;
  examReleaseDate?: string;
  inbondType?: string;
  inbondNumber?: string;
  inbondResponsible?: string;
  inbondRespOther?: string;
  inbondTenderRcvd?: string;
  whseEntryNum?: string;
  whse3461Date?: string;
  whseRedStampDate?: string;
  zeroRecordDate?: string;
  renderedDeadlineDate?: string;
  renderedDate?: string;
  temperatureControlled?: string;
  genset?: string;
  temperature?: string;
  tempScale?: string;
  ventSetting?: string;
  highValueCargo?: string;
  cargoValue?: number | null;
  hazmatClass?: string;
  hazmatPkg?: string;
  hazmatUn?: string;

  // Addl Info: Audit / Template / Reuse
  createdBy?: string;
  createdByName?: string;
  createdDatetime?: string;
  createdFrom?: string;
  parentId?: string;
  /** Count of non-deleted EDI records for this pro (V1 numedifiles). Gates the
   *  Addl Info "View EDI" link. */
  numEdiFiles?: number;
  template?: string;
  lastChangedBy?: string;
  lastChangedByName?: string;
  lastChangedDatetime?: string;
  masterPro?: string;
  templateType?: string;
  revenueRegion?: string;
  gwiMove?: string;
  sendEmailNotifications?: string;
  addlInfoDeleted?: string;
  reuseStatusUserName?: string;
  reuseRequestedUser?: string;
  reuseRequestedUserName?: string;
  reuseRequestedDatetime?: string;

  /** True when the order's dispatcher is a GWI dispatcher (d.gwi='Y') — gates
   *  the Available-to-Ship checkbox (legacy $order_isgwidisp). */
  orderIsGwiDispatcher?: boolean;
  /** Raw cstdispatcher username (the editable value behind the CST Dispatcher
   *  dropdown); cstDispatcher is the joined display name. */
  cstDispatcherUsername?: string;
  /** Rail-to-Port "Port Trace" terminal (porttrace column) — same option set as
   *  rail; shown beneath "Rail Trace" on RAIL TO PORT orders. */
  portTrace?: string;
  // Tracing tab
  traceContainer?: string;
  traceMode?: string;
  etaDate?: string;
  revisedEtaDatetime?: string;
  tracePortOfOrigin?: string;
  tracePortOfDischarge?: string;
  traceVessel?: string;
  traceVoyage?: string;
  vesselSailDateEst?: string;
  vesselSailDatetime?: string;
  vesselArrivalDateEst?: string;
  vesselArrivalDatetime?: string;
  loadingRamp?: string;
  trainDepartDatetime?: string;
  arrivalGroundedDatetime?: string;
  arrivalGroundedLocation?: string;
  bookingOnFileDate?: string;
  equipReleaseDate?: string;
  customsStatus?: string;
  customsRelDatetime?: string;
  lineStatus?: string;
  lineRelDatetime?: string;
  otherHolds?: string;
  availableGoodToGoDatetime?: string;
  thdAvailableDatetime?: string;
  railLfdDate?: string;
  outGateDatetime?: string;
  preAdviseDatetime?: string;
  hazReqDatetime?: string;
  railBillStatus?: string;
  /** 'Y' if rail billing is required for this move (scacinfo import/export flag). */
  railbillingReqd?: string;
  /** 'Y' if a rail bill has been sent and not cancelled (active railbillemail row). */
  railbillSent?: string;
  /** True when the move routes through a rail-applicable location, so the
   * Send/Cancel rail-billing buttons are relevant. Mirrors the v1
   * railbillinglocations qualification. */
  railBillingQualifies?: boolean;
  traceTerminateDatetime?: string;
  traceRunDatetime?: string;
  traceRunCarrier?: string;
  traceEventDatetime?: string;
  traceEvent?: string;
  traceLocation?: string;
  yardPullDate?: string;
  perDiemSslInvRcvDate?: string;

  // Export-specific
  documentCutoffDatetime?: string;
  customerCutoff?: string;
  // Customer Document Cutoff (Y/N) — repurposed `dvirlights` column, sits next to
  // documentCutoffDatetime. Distinct LC field from customerCutoff.
  custDocCut?: string;
  rolling?: string;
  rollingFault?: string;
  availToShipDatetime?: string;
  availToShipUser?: string;
  releasedSet?: string;
  schedulingContactType?: string;
  schedulingContactInfo?: string;
  railPickupContactType?: string;
  railPickupContactInfo?: string;
  tmwOrderNum?: string;

  // Addl Cost tab
  perDiemStartDate?: string;
  perDiemEndDate?: string;
  perDiemDaysFree?: number;
  perDiemDaysType?: string;
  perDiemDaysSource?: string;
  perDiemContractNumber?: string;
  perDiemAmount?: number;
  perDiemApproval?: string;
  perDiemReason?: string;
  perDiemFollowupDate?: string;
  /** Per-diem responsibility note (cms.orders_perdiemresponsible.perdiemnotes). */
  perDiemNotes?: string;
  /** Per-diem clock end fallback (terminate/delivery leg arrival, MM/DD/YYYY, or
   *  blank) — used as the end date when perdiemenddate is blank, before today. */
  perDiemFallbackEnd?: string;
  /** Whether the per-diem highlight is active (enddays - startdays > 0). */
  perDiemColorActive?: boolean;
  /** V1 chassis-usage window (e.g. "Tue 4/7/26 - Fri 7/3/26"); blank when the
   *  originate leg has no arrival datetime. */
  chassisUsage?: string;
  numChassisDays?: number | string;
  chassisFeeAmount?: number;
  chassisFeeResolved?: string;
  chassisFeeResolvedComment?: string;
  /** A CHASSIS MANAGEMENT invoice line (>0) exists — drives the Calc-Chassis-Fee
   *  red highlight + the Resolved read-only rule. */
  hasChassisFee?: boolean;
  /** Terminate leg has arrived (t_arrivaldatetime set) — suppresses the
   *  cutoff / doc-cutoff proximity highlight. */
  terminateLegArrived?: boolean;
  // Brokerage carrier fields — surfaced on the Addl Cost tab for brokerage dispatchers
  carrierRate?: number;
  carrierFsc?: number;
  carrierAccessorialRate?: number;
  carrierAccessorialComment?: string;
  addlCostDriverPayItems?: AddlCostDriverPayItem[];
  addlCostItems?: AddlCostItem[];
  addlCostTotalGwiPayment?: number;
  rejectedDriverPaperwork?: RejectedDriverPaperworkItem[];
  groundedInfo?: GroundedInfoItem[];
  hazmatInfo?: HazmatInfoItem[];
  hazmatApprovedBy?: string;
  hazmatApproval?: HazmatApproval;
  /** A non-deleted HAZ-MAT PAPERWORK document exists for the pro — a
   *  precondition for the Send Haz Docs button (V1 lib_editrecord.pl). */
  hasHazDocs?: boolean;

  // Invoicing tab
  margin?: number;
  marginLimitLocal?: number;
  marginRequestedBy?: string;
  marginRequestedDatetime?: string;
  marginApprovedBy?: string;
  marginApprovedDatetime?: string;
  rateComments?: string;
  freightTerms?: string;
  invoiceApprovedBy?: string;
  invoiceApprovedDatetime?: string;
  cod?: string;
  rebill?: string;
  /** Invoice hold reason (V1 ordersall.invoiceholdreason) — free text from a
   *  fixed dropdown; blank means no hold. */
  invoiceHoldReason?: string;
  /** Last follow-up on the invoice hold (V1 ordersall.invoiceholdfollowupdatetime),
   *  formatted "MM/DD/YYYY HH:MM"; blank when unset. */
  invoiceHoldFollowupDatetime?: string;
  requirePerDiemApproval?: string;
  missingInvoiceApprovedBy?: string;
  missingInvoiceApprovedReason?: string;
  billToMas90Code?: string;
  billToContactName?: string;
  /** Bill-to contact lookup (contactinfo where tablename='billtos',
   *  tablerecno=billTo). Recno pre-selects the dropdown; phone/email shown
   *  under it (mirrors brokerContact* on Addl Info). */
  billToContactRecno?: number;
  billToContactPhone?: string;
  billToContactEmail?: string;
  invoiceMain?: InvoiceMain | null;
  invoiceLineItems?: InvoiceLineItem[];
  /** Warehouse codes the pro worked through (receiving rows by pronumber or
   *  blpronumber, plus the BL rows the Whse tab shows), e.g. ['GWI08A']. Drives the
   *  GL Code highlight on the Invoicing tab and the Pending Approval→RESOLVED block
   *  (see invoiceLineItemShared.whseGlCodeMatches). Empty / absent on a template
   *  and on a pro with no MR/BL yet. */
  whseCodes?: string[];
  accessorialInvoices?: AccessorialInvoice[];

  legs: OrderLeg[];
  warnings?: { code: string; message: string }[];
  /** Count of NEW/PENDING trouble tickets on this order, matched by pronumber
   *  and by bookingnumber/BOL. Drives the Legs grid's dispatch warning. */
  openTtCount?: number;
  openTickets?: TroubleTicketEntry[];
  /** Count of open (OPEN/PENDING/DEFERRED/DISPATCHED) repair orders reaching
   *  this order by pronumber, container or chassis. Drives the Legs grid's
   *  dispatch warning alongside openTtCount. */
  openRoCount?: number;
  noteCount?: number;
  docCount?: number;
}

export interface OrderDetailsResponse {
  data: OrderDetails;
}

/** One row of a PRO's storage-cleared history (cms.storagecleared). */
export interface StorageClearedRecord {
  recno: number;
  clearedDate: string; // MM/DD/YYYY
  enteredBy: string;
  enteredAt: string; // MM/DD/YYYY HH:MM
}

export interface StreetTurnOpportunity {
  importPronumber?: number;
  exportPronumber?: number;
  customer: string;
  dropReleaseDatetime?: string;
  currentLocation?: string;
  driver?: string;
  termLocation?: string;
  destApptDate?: string;
  perDiemStartDate?: string;
  deliveryLocation?: string;
  legDrop?: string;
  origApptDate?: string;
  deliveryOption1Date?: string;
  firstRcvDate?: string;
  estDistance: number;
  /** Export-pro fields populated by /streetturn/import/{pronumber}; absent on
   * the export-side endpoint variant. */
  equipType?: string;
  flexBooking?: string;
}

export interface DriverValidation {
  isMule?: boolean;
  unitNumber: string;
  driverName: string;
  truckVin: string;
  isQualified: boolean;
  missingRequirements: string[];
  status: string;
  outOfServiceInfo: string | null;
  driveTimeRemaining?: string | null;
  attributes: {
    isDaycab: boolean;
    isHazmat: boolean;
    isTank: boolean;
    is93k: boolean;
    isMule: boolean;
    isoTankApproved: boolean;
    truckWeight: number;
    isLightweight?: boolean;
    hasPpe?: boolean;
  };
}

export interface DriverValidationResponse {
  drivers: DriverValidation[];
  message?: string;
}

/**
 * One leg's effective state, sent by the Order Details modal so the backend can
 * compute dispatch scope without reading legscurrent. Mirrors the LegContextItem
 * Pydantic model in driver_dispatch.py.
 */
export interface DriverValidationLegContextItem {
  legnum: number;
  legdrop: 'Y' | 'N' | string;
  destapptdate?: string | null; // YYYY-MM-DD
  origcode?: number | null;
  destcode?: number | null;
}

export interface DriverDispatchInfo {
  unitNumber: string;
  status: string;
  driverName: string;
  truckVin: string;
  driverStatus: string | null;
  dispatch: {
    proNumber: string;
    dispatchStarted: string | null;
    origin: {
      name: string;
      coordinates: string;
      arrivalDateTime: string | null;
      departDateTime: string | null;
    };
    destination: {
      name: string;
      coordinates: string;
      arrivalDateTime: string | null;
      departDateTime: string | null;
    };
    legDrop: string;
    nextDispatchId: number;
    isCompleted: boolean;
    size: string | null;
    equipType: string | null;
  };
}

export interface DriverDispatchResponse {
  drivers: DriverDispatchInfo[];
  cachedAt: string | null;
  totalCount: number;
}

export interface StreetTurnResponse {
  data: StreetTurnOpportunity[];
  count: number;
}

export interface OrdersResponse {
  data: Order[];
  count: number;
}

export interface NotSentToGwiMoveOrder {
  proNumber: number;
  apptDate: string;
  customer: string;
  origin: string;
  destination: string;
  driver: string;
  sentToGwiMove: boolean;
  region: string;
  legType: string;
  legnum: number;
  legrecno: number;
  origcode: number;
  destcode: number;
  origApptDate: string;
  destApptDate: string;
  unitNumber: string;
  moveType: string;
  container: string;
  size: string;
  equipType: string;
  linkedLegRecnos: number[];  // List of other legrecnos in the same dispatch group
  driverDispatcherUsername: string;  // Driver's dispatcher username (empty if no driver/dispatcher)
  driverDispatcher: string;          // Driver's dispatcher display name (empty if no driver/dispatcher)
}

export interface NotSentToGwiMoveResponse {
  data: NotSentToGwiMoveOrder[];
  count: number;
  /** Eligible pending legs before dashboard filters. Optional for older servers. */
  totalCount?: number;
}

export interface SendToGwiMoveItem {
  unitNumber: string;
  proNumber: number;
  legrecno: number;
  legnum: number;
}

export interface SendToGwiMoveResponse {
  success: number;
  failed: number;
  errors?: string[];
  message: string;
}

export interface RemoveFromDriverItem {
  proNumber: number;
  legnum: number;
  origcode: number;
  destcode: number;
  origApptDate: string;
  destApptDate: string;
  driver: string;  // Will be empty string to remove driver
  oldDriver?: string;  // Previous driver (for note formatting)
  notes?: string;  // Reason note for deadline moves
}

export interface RemoveFromDriverResponse {
  success: number;
  failed: number;
  errors?: string[];
  failedItems?: { proNumber: number; legnum: number }[];
  message: string;
}

export interface CheckDeadlineItem {
  proNumber: number;
  legnum: number;
  oldDriver: string;
  newDriver: string;
}

export interface QualifyingDetail {
  pronumber: number;
  legnum: number;
  driver: string;
  dispatch_id: number | null;
  appt_date: string | null;
  deadline_type: 'LFD' | 'Per Diem' | 'Cutoff';
  deadline_date: string | null;
}

export interface CheckDeadlineResponse {
  requiresNote: boolean;
  qualifyingPros: number[];
  qualifyingDetails: QualifyingDetail[];
}

export interface Dispatcher {
  username: string;
  displayname: string;
}

export interface AllDispatcher {
  username: string;
  displayname: string;
  gwi: boolean;
}

export interface Customer {
  recno: number;
  name: string;
  keyAccount: string;
}

export interface DispatcherGroup {
  groupname: string;
  dispatcherlist: string;
}

export interface DispatchersResponse {
  data: Dispatcher[];
  groups?: DispatcherGroup[];
  count: number;
}

export interface CustomersResponse {
  data: Customer[];
  count: number;
}

export interface Region {
  region: string;
}

export interface ChassisEquipment {
  unit: string;
  license: string;
  model: string;
  condition: string;
  company: string;
  triaxle: string;
  /** True when BlackBerry Radar reports coordinates for this unit. Drives the
   *  red/green chassis-location pin on the order form. Optional so older cached
   *  responses (and test fixtures) still type-check. */
  hasRadar?: boolean;
}

export interface VanEquipment {
  unit: string;
  license: string;
  model: string;
  condition: string;
  company: string;
}

export interface RegionsResponse {
  data: {
    regions: string[];
    revenueRegions: string[];
  };
  count: {
    regions: number;
    revenueRegions: number;
  };
}

/** Change Template availability for the Addl Info trigger (V1 changetemplate_validate). */
export interface ChangeTemplateEligibility {
  eligible: boolean;
  reasons: string[];
  /** Set when the order's delivery leg has already arrived: the template search must
   *  pin (and lock) this delivery location — a delivered order can only change to a
   *  same-delivery-location template (V1 changetemplate_lookup readonly + validate). */
  deliveryLock?: { code: number; name: string } | null;
}

/** One approved-template candidate row (V1 changetemplate_results grid). Shares
 *  the Templates-page column shape (see backend _template_sql). */
export interface ChangeTemplateCandidate {
  pronumber: string | number;
  movetype?: string | null;
  ratezone?: string | null;
  quotenumber?: string | number | null;
  csr?: string | null;
  whsecsr?: string | null;
  dispatcher?: string | null;
  size?: string | null;
  equiptype?: string | null;
  isdrop?: string | null;
  otr?: string | null;
  hazmat?: string | null;
  billto?: string | null;
  customer?: string | null;
  pickup?: string | null;
  delivery?: string | null;
  comments?: string | null;
  approvedby?: string | null;
  approveddatetime?: string | null;
  [k: string]: unknown;
}

/** One per-field change in the Change Template summary (V1 changetemplate_options). */
export interface ChangeTemplateFieldDiff {
  field: string;
  label: string;
  old: string;
  new: string;
  /** true = user may toggle apply; false = mandatory (always applied). */
  optional: boolean;
}

export interface ChangeTemplateLineDiff {
  line: number;
  old: string;
  new: string;
}

/**
 * What the apply will do to a pro's legs (bugfix/#34227) — built server-side from
 * the same plan the apply runs. 'replaced': no driver, so the template's legs are
 * copied in. 'kept': a driver is assigned, so the legs stay and the template's
 * layout is NOT applied. `message` names locations (single-order notice);
 * `group` is location-free so the bulk review can count pros by outcome.
 */
export interface ChangeTemplateLegChange {
  kind: 'replaced' | 'kept';
  fromLegs: number;
  toLegs: number;
  message: string;
  group: string;
}

export interface ChangeTemplatePreviewRow {
  pronumber: number | string;
  eligible: boolean;
  errors: string[];
  diffs: ChangeTemplateFieldDiff[];
  invoiceLineDiffs: ChangeTemplateLineDiff[];
  warnings: string[];
  /** Absent on ineligible rows; null when the stops already match. */
  legChange?: ChangeTemplateLegChange | null;
}

export interface ChangeTemplateApplyResult {
  pronumber: number | string;
  applied: boolean;
  errors: string[];
  template?: string;
  /** 'driver' (relocation) | 'nodriver' (wipe + recopy legs). */
  legMode?: string;
  /** Post-save alert()s surfaced by rules (v1 %usermessage). */
  userMessages?: string[];
}

export interface ChangeAllChange { column: string; newValue: any; }
export interface ChangeAllLegChange {
  /** How the backend resolves which leg on each target pro to write:
   *  role first (originate/delivery/terminate), else by legnum. */
  match: { role?: 'originate' | 'delivery' | 'terminate'; legnum?: number };
  column: string;
  newValue: any;
}
export type GridEditRole = 'yn' | 'number' | 'date' | 'datetime' | 'text' | 'select';

/** One editable Search Results column: the widget, what the edit is keyed by, and the
 *  underlying column the save writes. See pages/searchResults/orderGridEdit. */
export interface GridFieldSpec {
  role: GridEditRole;
  scope: 'order' | 'leg' | 'invoice' | 'mainInvoice';
  column: string;
  legRole?: string;
  component?: string;
  /** Fixed option list for a `select` field, as {value, label}. The two are the same
   *  for a coded vocabulary ('ON'); they differ for a ROSTER, where the value is what
   *  gets stored (`lmarinelli`) and the label is what /pro displays ("Lee Marinelli").
   *  Resolved server-side, which is also what lets the save validate against it. */
  options?: { value: string; label: string }[];
  /** Templates binding only: the template field policy's state for this field —
   *  3 = always editable, 2 = editable only while the template is UNAPPROVED. The grid
   *  gates the cell against the ROW's own approval, so it needs the distinction rather
   *  than a single yes/no. Absent for the orders binding. */
  templateState?: 2 | 3;
}

export interface ChangeAllChangeSet {
  /** ordersall columns (Phase 1). */
  order?: ChangeAllChange[];
  /** invoice-table columns (main invoice row: accessorialnum=0, deleted='N'). */
  invoice?: ChangeAllChange[];
  /** per-leg edits, resolved to a leg on each target pro by role/legnum. */
  legs?: ChangeAllLegChange[];
  /** Free-text reason from the delete modal, present only when `order` carries a
   *  `deleted='Y'` flip. Propagated to every fanned pro (the backend threads it into
   *  the native order_delete rule as the delete reason — note/history/email). */
  deleteReason?: string;
  /** Per Diem Responsibility note from the prompt behind the Responsible dropdown,
   *  present only when `order` carries a `perdiemapproval` change. Applied to every
   *  fanned pro as orders_perdiemresponsible.perdiemnotes + its history row + the
   *  'PER DIEM APPROVAL:' pro note. Requires the caller to hold the single-pro
   *  /orders/perdiem-notes grant (Developers + LCs), which the server re-checks. */
  perDiemNotes?: string;
}
export interface ChangeAllPreviewRow {
  pronumber: string | number;
  eligible: boolean;
  errors: string[];
  /** Non-blocking caveats for an ELIGIBLE pro (e.g. an invoice line whose position
   *  holds a different charge on this pro — that line is skipped, the rest applies). */
  warnings?: string[];
}
export interface ChangeAllApplyResult {
  pronumber: string | number;
  applied: boolean;
  errors: string[];
  /** Non-blocking caveats on a pro that DID change (e.g. the ticket was created but
   *  its reschedule was skipped because a leg in the dispatch group has departed). */
  warnings?: string[];
}

// --- LC Dashboard "Clear All Leg Appt Dates" (Past Due - Not Dispatched bucket) ---
export interface ClearApptDatesLegClear {
  legnum: number;
  /** current normalized YYYY-MM-DD, or '' when that side has nothing to clear. */
  origApptDate: string;
  destApptDate: string;
  /** the leg's move actions — origin action (legtype) and destination action (legaction2). */
  legType: string;
  legAction2: string;
}
export interface ClearApptDatesPreviewRow {
  pronumber: string | number;
  clears: ClearApptDatesLegClear[];
  /** legs left untouched because a driver is assigned somewhere in the dispatch scope. */
  skippedLegnums: number[];
  /** a service failure will be created silently for this pro on save (delivery appt removed). */
  serviceFailureRequired: boolean;
  error: string | null;
}
export interface ClearApptDatesPreview {
  rows: ClearApptDatesPreviewRow[];
  /** at least one pro will get a service failure created on save. */
  serviceFailureRequired: boolean;
}
export interface ClearApptDatesApplyResult extends ChangeAllApplyResult {
  clearedLegnums?: number[];
  skippedLegnums?: number[];
  serviceFailure?: boolean;
  skipped?: boolean;
}

/**
 * One row of the Dupe Order per-new-pro grid — V1's `@dupescreen` columns in
 * display order (backend `_DUPE_SCREEN_FIELDS` / `order_dupe.DUPE_ROW_FIELDS`).
 * Every field is an optional string because a blank value means "inherit the
 * source order" server-side (except container/lastfreedaydate, which the backend
 * deliberately never inherits), so the client sends only what the user typed.
 */
export interface DupeOrderRow {
  container?: string;
  bol?: string;
  sealnumber?: string;
  chassisid?: string;
  commodity?: string;
  piececount?: string;
  lbs?: string;
  kgs?: string;
  metrictons?: string;
  po?: string;
  billtorefno?: string;
  etadate?: string;
  targetonorby?: string;
  targetdeliverydate?: string;
  lastfreedaydate?: string;
  cutoffdatetime?: string;
  origapptnumber?: string;
  destapptnumber?: string;
}

const ordersService = {
  /**
   * Get import orders for dispatching dashboard
   */
  getImports: async (dispatcher?: string, customer?: number | string, region?: string, otr?: string, live?: string, size?: string, containertype?: string, chassistype?: string, options?: { signal?: AbortSignal; additionalFields?: string }): Promise<OrdersResponse> => {
    const params = new URLSearchParams();
    if (dispatcher) params.append('dispatcher', dispatcher);
    if (customer != null && customer !== '') params.append('customer', String(customer));
    if (region) params.append('region', region);
    if (otr) params.append('otr', otr);
    if (live) params.append('live', live);
    if (size) params.append('size', size);
    if (containertype) params.append('containertype', containertype);
    if (chassistype) params.append('chassistype', chassistype);
    if (options?.additionalFields) params.append('additional_fields', options.additionalFields);

    const response = await api.get(`/orders/imports${params.toString() ? `?${params.toString()}` : ''}`, { signal: options?.signal });
    return response.data;
  },

  /**
   * Get export orders for dispatching dashboard
   */
  getExports: async (dispatcher?: string, customer?: number | string, region?: string, otr?: string, live?: string, size?: string, containertype?: string, chassistype?: string, options?: { signal?: AbortSignal; additionalFields?: string }): Promise<OrdersResponse> => {
    const params = new URLSearchParams();
    if (dispatcher) params.append('dispatcher', dispatcher);
    if (customer != null && customer !== '') params.append('customer', String(customer));
    if (region) params.append('region', region);
    if (otr) params.append('otr', otr);
    if (live) params.append('live', live);
    if (size) params.append('size', size);
    if (containertype) params.append('containertype', containertype);
    if (chassistype) params.append('chassistype', chassistype);
    if (options?.additionalFields) params.append('additional_fields', options.additionalFields);

    const response = await api.get(`/orders/exports${params.toString() ? `?${params.toString()}` : ''}`, { signal: options?.signal });
    return response.data;
  },

  /**
   * Get drop released container orders for dispatching dashboard
   */
  getDropReleased: async (dispatcher?: string, customer?: number | string, region?: string, revenueregion?: string, otr?: string, size?: string, containertype?: string, chassistype?: string, options?: { signal?: AbortSignal; additionalFields?: string; notDispatched?: boolean }): Promise<OrdersResponse> => {
    const params = new URLSearchParams();
    if (options?.notDispatched) params.append('not_dispatched', 'true');
    if (dispatcher) params.append('dispatcher', dispatcher);
    if (customer != null && customer !== '') params.append('customer', String(customer));
    if (region) params.append('region', region);
    if (revenueregion) params.append('revenueregion', revenueregion);
    if (otr) params.append('otr', otr);
    if (size) params.append('size', size);
    if (containertype) params.append('containertype', containertype);
    if (chassistype) params.append('chassistype', chassistype);
    if (options?.additionalFields) params.append('additional_fields', options.additionalFields);

    const response = await api.get(`/orders/dropreleased${params.toString() ? `?${params.toString()}` : ''}`, { signal: options?.signal });
    return response.data;
  },

  /**
   * Get orders not sent to gwiMove
   */
  getNotSentToGwiMove: async (dispatcher?: string, customer?: number | string, region?: string, otr?: string, options?: { signal?: AbortSignal }): Promise<NotSentToGwiMoveResponse> => {
    const params = new URLSearchParams();
    if (dispatcher) params.append('dispatcher', dispatcher);
    if (customer != null && customer !== '') params.append('customer', String(customer));
    if (region) params.append('region', region);
    if (otr) params.append('otr', otr);

    const response = await api.get(`/orders/not-sent-to-gwimove${params.toString() ? `?${params.toString()}` : ''}`, { signal: options?.signal });
    return response.data;
  },

  /**
   * Get list of order dispatchers for filtering (dispatchers with current orders)
   */
  getOrderDispatchers: async (signal?: AbortSignal): Promise<DispatchersResponse> => {
    // Dispatcher roster: reference data mounted by filter components on ~10
    // pages — shared 5-min cache instead of a fetch per page navigation.
    const response = await cachedGet<DispatchersResponse>('/orders/dispatchers', { signal });
    return response.data;
  },

  /**
   * Get full list of non-deleted dispatchers (GWI and non-GWI) for assigning a dispatcher to an order.
   */
  getAllDispatchers: async (signal?: AbortSignal): Promise<{ data: AllDispatcher[]; count: number }> => {
    const response = await api.get('/orders/all-dispatchers', { signal });
    return response.data;
  },

  /**
   * Get list of driver dispatchers for filtering (dispatchers with active drivers)
   */
  getDriverDispatchers: async (signal?: AbortSignal): Promise<DispatchersResponse> => {
    const response = await cachedGet<DispatchersResponse>('/safety/dispatchers', { signal });
    return response.data;
  },

  /**
   * Get list of customers for filtering
   */
  getCustomers: async (dispatcher?: string): Promise<CustomersResponse> => {
    const params = new URLSearchParams();
    if (dispatcher) params.append('dispatcher', dispatcher);

    const response = await api.get(`/orders/customers${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  },

  /**
   * Get list of regions for filtering
   */
  getRegions: async (): Promise<RegionsResponse> => {
    // 12 call sites, one per page mount — shared cache.
    const response = await cachedGet<RegionsResponse>('/orders/regions');
    return response.data;
  },

  /**
   * Get all dispatch dashboard filter dropdown options in a single request.
   * Returns dispatchers, customers, and regions to reduce HTTP connections.
   */
  getDispatchFilterOptions: async (): Promise<{
    dispatchers: { username: string; displayname: string }[];
    customers: { recno: number; name: string; keyAccount: string }[];
    regions: { regions: string[]; revenueRegions: string[] };
  }> => {
    const response = await api.get('/orders/dispatch-filter-options');
    return response.data;
  },

  /**
   * Get detailed information about a specific order
   */
  getOrderDetails: async (
    pronumber: number | string,
    signal?: AbortSignal,
  ): Promise<OrderDetailsResponse> => {
    // Accepts a "T"-prefixed template pronumber (e.g. "T110") as well as a
    // numeric order pronumber — the backend reads templates from the
    // template* tables under the same route. The string is interpolated
    // verbatim, so "T110" is preserved (never collapsed to 110).
    const response = await api.get(`/orders/order-details/${pronumber}`, { signal });
    return response.data;
  },

  /**
   * Ordered list of PRO numbers sharing this order's masterpro (the open-entry
   * grouping field), for Master Pro prev/next navigation. `masterPro` is the
   * empty string and `pronumbers` empty when the order has no masterpro set.
   */
  getMasterproSiblings: async (
    pronumber: number,
    signal?: AbortSignal,
  ): Promise<{ masterPro: string; pronumbers: number[] }> => {
    const response = await api.get(`/orders/${pronumber}/masterpro-siblings`, { signal });
    return response.data;
  },

  /**
   * Storage-cleared history for a PRO (cms.storagecleared rows, newest first).
   * Append-only — the rows are the history.
   */
  getStorageCleared: async (
    pronumber: number,
    signal?: AbortSignal,
  ): Promise<{ records: StorageClearedRecord[] }> => {
    const response = await api.get(`/orders/${pronumber}/storage-cleared`, { signal });
    return response.data;
  },

  /**
   * Append a storage-cleared-through date (YYYY-MM-DD) for a PRO. Creates one
   * storagecleared row + a matching order-history row.
   */
  addStorageCleared: async (
    pronumber: number,
    newDate: string,
  ): Promise<{ success: boolean }> => {
    const response = await api.post(`/orders/${pronumber}/storage-cleared`, { newDate });
    return response.data;
  },

  /** Add a grounded-dates row (cms.groundeddates). Location + startDate required. */
  addGroundedDate: async (
    pronumber: number,
    payload: GroundedDateCreate,
  ): Promise<{ success: boolean; recno: number }> => {
    const response = await api.post(`/orders/${pronumber}/grounded-dates`, payload);
    return response.data;
  },

  /** Edit an existing grounded-dates row. Send only changed fields (endDate:'' clears). */
  updateGroundedDate: async (
    pronumber: number,
    recno: number,
    payload: GroundedDateUpdate,
  ): Promise<{ success: boolean; changed: boolean }> => {
    const response = await api.patch(`/orders/${pronumber}/grounded-dates/${recno}`, payload);
    return response.data;
  },

  /** Soft-delete a grounded-dates row (deleted='Y'). */
  deleteGroundedDate: async (
    pronumber: number,
    recno: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(`/orders/${pronumber}/grounded-dates/${recno}`);
    return response.data;
  },

  /**
   * Look up hazmat reference lines from cms.hazmatdata by UN prefix (backs the
   * inline UN lookup on the Addl Info hazmat add-row). V1 main.pl::hazmatlookup.
   */
  hazmatLookup: async (
    un: string,
    signal?: AbortSignal,
  ): Promise<{ results: HazmatLookupItem[] }> => {
    const response = await api.get(`/orders/hazmat-lookup`, { params: { un }, signal });
    return response.data;
  },

  /**
   * Add one hazmat line to a pro (immediate save). Recomputes the order hazmat
   * flag and clears/warns approval server-side. V1 main.pl::savehazmatinfo.
   */
  addHazmatLine: async (
    pronumber: number | string,
    line: {
      hazmatUn: string;
      shippingName: string;
      hazmatClass: string;
      hazmatPkg: string;
      limitedQty: string;
    },
  ): Promise<{ success: boolean; hazmat: string; approvalCleared: boolean }> => {
    const response = await api.post(`/orders/${pronumber}/hazmat-lines`, line);
    return response.data;
  },

  /**
   * Soft-delete one hazmat line. Recomputes the order hazmat flag.
   * V1 main.pl::deletehazmat.
   */
  deleteHazmatLine: async (
    pronumber: number | string,
    recno: number,
  ): Promise<{ success: boolean; hazmat: string }> => {
    const response = await api.delete(`/orders/${pronumber}/hazmat-lines/${recno}`);
    return response.data;
  },

  /**
   * Whether Change Template is available for this order + reasons if not.
   * Ports the order-only checks of V1 main.pl::changetemplate_validate.
   */
  changeTemplateEligibility: async (
    pronumber: number | string,
    signal?: AbortSignal,
  ): Promise<ChangeTemplateEligibility> => {
    const response = await api.get(
      `/orders/${pronumber}/change-template/eligibility`,
      { signal },
    );
    return response.data;
  },

  /**
   * Approved templates matching bill-to/customer (+optional move-type / delivery
   * location) for the Change Template search. V1 main.pl::changetemplate_results.
   */
  changeTemplateCandidates: async (
    params: {
      billto: number;
      customer: number;
      movetype?: string;
      deliverto?: number;
      /** Free-text delivery-location name filter (LIKE '%…%' against the
       *  delivery leg's location) — Create New Order's template picker
       *  "Deliver To Filter" field. Coexists with the recno `deliverto` above. */
      deliverto_name?: string;
      /** The pro's own template — always returned, filters or not, so an order
       *  can be re-applied to its template after the template was edited. */
      current_template?: string;
      /** Orders-scope registry fieldnames to attach as extra columns, resolved
       *  against the TEMPLATE tables — the same picker the Templates board offers.
       *  Sent comma-joined in the user's selection order (the server keeps it). */
      additionalFields?: string[];
    },
    signal?: AbortSignal,
  ): Promise<{ rows: ChangeTemplateCandidate[]; capped: boolean }> => {
    const { additionalFields, ...rest } = params;
    const response = await api.get(`/orders/change-template/candidates`, {
      params: {
        ...rest,
        ...(additionalFields?.length
          ? { additional_fields: additionalFields.join(',') }
          : {}),
      },
      signal,
    });
    return response.data;
  },

  /**
   * Per-pro change summary (field diff + invoice-line diff + warnings, or the
   * blocking reasons) vs a chosen template. V1 changetemplate_options.
   */
  changeTemplatePreview: async (
    body: { template: number | string; pros: (number | string)[] },
    signal?: AbortSignal,
  ): Promise<{ template: number | string; rows: ChangeTemplatePreviewRow[] }> => {
    const response = await api.post(`/orders/change-template/preview`, body, { signal });
    return response.data;
  },

  /**
   * Apply a template change to the given pro(s) via the native save pipeline.
   * V1 changetemplate_savechanges + changetemplate_updateall. `submitNonce`
   * (one per Save click) makes the apply idempotent against a double-fire.
   */
  changeTemplateApply: async (
    body: {
      template: number | string;
      pros: (number | string)[];
      selectedOptional?: string[];
      submitNonce?: string;
    },
    signal?: AbortSignal,
  ): Promise<{ results: ChangeTemplateApplyResult[] }> => {
    const response = await api.post(`/orders/change-template/apply`, body, { signal });
    return response.data;
  },

  /**
   * Per-pro eligibility for a bulk field change (read-only). Mirrors
   * change-template preview; backend re-validates each pro. Change All uses the
   * `updateall` savesource in backend/app/services/saverules.
   */
  changeAllPreview: async (
    body: { pros: (number | string)[]; changes: ChangeAllChangeSet },
    signal?: AbortSignal,
  ): Promise<{ rows: ChangeAllPreviewRow[] }> => {
    const response = await api.post(`/orders/change-all/preview`, body, { signal });
    return response.data;
  },

  /**
   * Apply the flagged field changes to the given pro(s), each in its own
   * transaction (SaveSource.UPDATE_ALL). `submitNonce` = one per Save click.
   */
  changeAllApply: async (
    body: { pros: (number | string)[]; changes: ChangeAllChangeSet; submitNonce?: string },
    signal?: AbortSignal,
  ): Promise<{ results: ChangeAllApplyResult[] }> => {
    const response = await api.post(`/orders/change-all/apply`, body, { signal });
    return response.data;
  },

  /**
   * Apply a DISTINCT ChangeSet to each pro (a loop of individual updateall saves, not
   * one fan-out) — every order keeps its own value. Used by the LC Dashboard inline-edit
   * surfaces (e.g. the No Pickup Number bucket's editable pickup #). History is tagged
   * 'LC Dashboard Edit' server-side; `submitNonce` = one per Save click.
   */
  changeAllApplyPerPro: async (
    body: { items: { pronumber: number | string; changes: ChangeAllChangeSet }[]; submitNonce?: string },
    signal?: AbortSignal,
  ): Promise<{ results: ChangeAllApplyResult[] }> => {
    const response = await api.post(`/orders/change-all/apply-per-pro`, body, { signal });
    return response.data;
  },

  /**
   * Order columns the Search Results grid may bulk-edit, as {column: role} where role
   * names the widget ('yn' | 'number' | 'date' | 'datetime'). A column the grid may not
   * edit is simply absent. Static for the life of the deployment, so it rides the
   * shared lookup cache instead of refetching per search.
   */
  changeAllGridFields: async (
    signal?: AbortSignal,
    binding: 'orders' | 'templates' = 'orders',
  ): Promise<Record<string, GridFieldSpec>> => {
    // Two answers from one endpoint, so two cache entries — the URL carries the
    // binding, which is what keeps the Templates board's narrower set from being
    // served to Search Results (or the reverse) out of the shared lookup cache.
    const res = await cachedGet<{ fields: Record<string, GridFieldSpec> }>(
      `/orders/change-all/grid-fields?binding=${binding}`, { signal });
    return res.data?.fields ?? {};
  },

  /**
   * Apply staged Search Results grid edits — ONE request, three arms, because an edit
   * is keyed by what its field belongs to: an order by pronumber, a leg by pronumber +
   * role, an accessorial invoice by its own recno. Order and leg edits for the same pro
   * are merged into one save server-side. History is tagged 'Search Results Edit'.
   */
  changeAllGridEdit: async (
    body: {
      items?: { pronumber: number | string; values: Record<string, unknown> }[];
      legs?: { pronumber: number | string; legRole: string; values: Record<string, unknown> }[];
      invoices?: { recno: number; pronumber: number | string; values: Record<string, unknown> }[];
      mainInvoices?: { pronumber: number | string; values: Record<string, unknown> }[];
      submitNonce?: string;
    },
    signal?: AbortSignal,
  ): Promise<{ results: ChangeAllApplyResult[] }> => {
    const response = await api.post(`/orders/change-all/grid-edit`, body, { signal });
    return response.data;
  },

  /**
   * Staged-clear preview for the Past Due - Not Dispatched bucket's "Clear All Leg
   * Appt Dates" confirm dialog: per pro, the legs/dates that would clear and any legs
   * skipped for an assigned driver, plus whether the mandatory service-failure (and
   * THD reason) prompt is needed. Read-only.
   */
  clearApptDatesPreview: async (
    pros: (number | string)[],
    signal?: AbortSignal,
  ): Promise<ClearApptDatesPreview> => {
    const response = await api.post(`/orders/lc-dashboard/clear-appt-dates/preview`, { pros }, { signal });
    return response.data;
  },

  /**
   * Clear origapptdate+destapptdate on every leg of each pro's driver-free, past-due
   * dispatch scopes (times preserved), routed through the native saverules engine and
   * tagged 'LC Dashboard Edit'. For every pro whose delivery appointment is removed, an
   * uncoded service failure is created silently (SF date = the removed appt date) for
   * the LC to code later. `submitNonce` = one per Confirm click.
   */
  clearApptDatesApply: async (
    body: { pros: (number | string)[]; submitNonce?: string },
    signal?: AbortSignal,
  ): Promise<{ results: ClearApptDatesApplyResult[] }> => {
    const response = await api.post(`/orders/lc-dashboard/clear-appt-dates/apply`, body, { signal });
    return response.data;
  },

  /**
   * Per-pro eligibility for a bulk note-append (read-only). Notes have ~no
   * eligibility constraints, so the backend returns every pro eligible; the
   * pair exists so the shared review modal can reuse its preview step.
   */
  changeAllNotesPreview: async (
    body: { pros: (number | string)[]; notes: string },
    signal?: AbortSignal,
  ): Promise<{ rows: ChangeAllPreviewRow[] }> => {
    const response = await api.post(`/orders/change-all/notes/preview`, body, { signal });
    return response.data;
  },

  /**
   * Append the SAME note as a new note row on every target pro, each in its own
   * transaction. submitNonce+(pronumber) idempotency is required (an INSERT
   * re-fire would duplicate notes).
   */
  changeAllNotesApply: async (
    body: { pros: (number | string)[]; notes: string; submitNonce?: string },
    signal?: AbortSignal,
  ): Promise<{ results: ChangeAllApplyResult[] }> => {
    const response = await api.post(`/orders/change-all/notes/apply`, body, { signal });
    return response.data;
  },

  /**
   * Email the pro's HAZ-MAT PAPERWORK docs to the CSR / dispatcher / haz@poha.com
   * and stamp hazreqdatetime. V1 lib_editrecord.pl::sendhazdocs.
   */
  sendHazDocs: async (
    pronumber: number | string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/orders/${pronumber}/haz-docs/send`);
    return response.data;
  },

  /**
   * Get street turn opportunities for an export order
   */
  getExportStreetTurn: async (pronumber: number | string): Promise<StreetTurnResponse> => {
    const response = await api.get(`/streetturn/export/${pronumber}`);
    return response.data;
  },

  /**
   * Get street turn opportunities for an import order
   */
  getImportStreetTurn: async (pronumber: number | string): Promise<StreetTurnResponse> => {
    const response = await api.get(`/streetturn/import/${pronumber}`);
    return response.data;
  },

  /**
   * Validate if a driver is qualified for a specific order/leg
   */
  validateDriver: async (
    pronumber: number,
    unitNumber: string,
    legnum?: number | null,
    exactSearch?: boolean,
    signal?: AbortSignal,
  ): Promise<DriverValidationResponse> => {
    const queryParams = new URLSearchParams();
    if (legnum !== undefined && legnum !== null) queryParams.append('legnum', legnum.toString());
    if (exactSearch !== undefined) queryParams.append('exact_search', exactSearch.toString());
    const params = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(
      `/driver-dispatch/validate-driver/${pronumber}/${unitNumber}${params}`,
      { signal },
    );
    return response.data;
  },

  /**
   * Validate a driver against a frontend-provided list of legs instead of legscurrent.
   * Used by the Order Details modal where pending edits (new legs, renumbered legs,
   * edited locations or appt dates) haven't been saved yet — the GET variant would
   * either 404 on new legs or use stale DB state. legnum is required (it identifies
   * which leg in the context list is the target).
   */
  validateDriverWithContext: async (
    pronumber: number,
    unitNumber: string,
    legnum: number,
    legContext: DriverValidationLegContextItem[],
    exactSearch?: boolean,
    /**
     * Optional pre-save overrides for order-level fields. Merged onto the
     * saved order on the backend before qualification runs — used by the
     * overweight recheck flow to validate drivers against a freshly-flipped
     * `overweightPermitRequired` without forcing a save first.
     */
    orderOverrides?: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<DriverValidationResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('legnum', legnum.toString());
    if (exactSearch !== undefined) queryParams.append('exact_search', exactSearch.toString());
    const body: Record<string, unknown> = { leg_context: legContext };
    if (orderOverrides) body.order_overrides = orderOverrides;
    const response = await api.post(
      `/driver-dispatch/validate-driver/${pronumber}/${unitNumber}?${queryParams.toString()}`,
      body,
      { signal },
    );
    return response.data;
  },

  /**
   * Find all qualified drivers for a specific order/leg.
   *
   * Switches to the POST variant when `orderOverrides` OR `legContext` is
   * supplied so the backend evaluates qualifications against the caller's
   * pre-save edits AND uses the same leg-info builder the validate path
   * uses. Without either, hits the GET variant and uses the saved order
   * state. Callers should pass both together — `destapptdate` (and
   * therefore the cached `is93k` evaluation) only stays aligned with the
   * cell's validate result when both endpoints share the same leg
   * snapshot.
   */
  findValidDrivers: async (
    pronumber: number,
    legnum?: number,
    orderOverrides?: Record<string, unknown>,
    legContext?: DriverValidationLegContextItem[],
    signal?: AbortSignal,
  ): Promise<DriverValidationResponse & { count: number; orderRequirements: any }> => {
    const params = legnum ? `?legnum=${legnum}` : '';
    const url = `/driver-dispatch/find-valid-drivers/${pronumber}${params}`;
    const hasOverrides = orderOverrides && Object.keys(orderOverrides).length > 0;
    const hasLegContext = legContext && legContext.length > 0;
    if (hasOverrides || hasLegContext) {
      const body: Record<string, unknown> = {};
      if (hasOverrides) body.order_overrides = orderOverrides;
      if (hasLegContext) body.leg_context = legContext;
      const response = await api.post(url, body, { signal });
      return response.data;
    }
    const response = await api.get(url, { signal });
    return response.data;
  },

  /**
   * Get latest dispatch information for drivers
   */
  getLatestDispatches: async (unitNumber?: string, status?: string): Promise<DriverDispatchResponse> => {
    const params = new URLSearchParams();
    if (unitNumber) params.append('unitnumber', unitNumber);
    if (status) params.append('status', status);

    const response = await api.get(`/driver-dispatch/latest-dispatches${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  },

  /**
   * Dispatch a driver to an order/leg
   */
  dispatchDriver: async (pronumber: number, legnum: number, origcode: number, destcode: number, driver: string): Promise<any> => {
    const response = await api.post('/driver-dispatch/dispatch-drivers', [
      {
        pronumber,
        legData: [{ legnum, origcode, destcode, driver }]
      }
    ]);
    return response.data;
  },

  /**
   * Update leg details (driver, appointment dates, sendToGwiMove)
   * and optional order-level fields (e.g., chassisprovider)
   */
  updateLegDetails: async (pronumber: number, legData: any[], data?: any, notes?: string): Promise<any> => {
    const payload: any = { pronumber, legData };
    if (data) payload.data = data;
    if (notes) payload.notes = notes;
    const response = await api.post('/orders/update-leg-details', payload);
    return response.data;
  },

  /**
   * Container+BOL duplicate check for the Create New Order modal's "Continue"
   * step (V1 lib_editrecord.pl:19549 — only runs when Equipment No is
   * non-blank). Same `check_for_order_dupes` helper the create endpoint
   * re-runs at save time. Blank container short-circuits server-side to
   * `{dupes: []}` without querying.
   */
  checkDuplicates: async (
    params: { container: string; bol: string },
    signal?: AbortSignal,
  ): Promise<{ dupes: Array<string | number> }> => {
    const response = await api.get('/orders/check-duplicates', { params, signal });
    return response.data;
  },

  /**
   * Create a new order — one atomic transaction (allocate pronumber, skeleton
   * ordersall/orderscurrent + invoice rows, native saverules engine run with
   * is_new_order=True, template child-row copies). The backend owns the
   * authoritative template `template.*` copy as the base, applies the modal
   * overrides, then applies `orderData` (drawer edits) on top; `legs` and
   * `invliData` are sent as AUTHORITATIVE full structures (the user saw/edited
   * them) — the backend inserts them instead of copying the template's.
   * `submitNonce`/`bypassDupeCheck` follow the same idempotency + dupe-bypass
   * convention as `saveOrderDetails`. Mirrors that call's response shape:
   * `requiresConfirmation`/`dupes` on a duplicate block (nothing written),
   * `pronumber`/`userMessages` on success.
   */
  createOrder: async (payload: {
    movetype: string;
    billto: number;
    customercode: number;
    bol: string;
    container: string;
    templatepro?: string; // 'T###' — createdfrom/parentid/template + server-side child-row copies
    bypassDupeCheck?: boolean; // modal bypass, carried through the save-time re-check
    submitNonce: string; // idempotency, same convention as saveOrderDetails
    originateleg: number;
    deliveryleg: number;
    terminateleg: number;
    legs: LegSnapshotItem[]; // buildLegSavePayload({ originalLegs: [] }) — every leg is "new"
    orderData?: OrderDataRow[]; // buildOrderDataRows(editedOrderFields, seed) — drawer edits only
    invoiceData?: OrderDataRow[]; // buildInvoiceDataRows(editedOrderFields, seed)
    // Full authoritative main-invoice line-item set. Same inline shape
    // useOrderEditing's save() builds today (useOrderEditing.ts:2434) — not a
    // named export there, so it's mirrored here rather than imported.
    invliData: Array<{ recno: number | null; new: any; old: any }>;
    notes?: string[]; // staged notes (Create mode's NotesTab closures)
  }): Promise<{
    pronumber: number;
    requiresConfirmation?: boolean;
    dupes?: Array<string | number>;
    userMessages?: Array<{ text: string; level: 'error' | 'warning' | 'info' }>;
  }> => {
    const response = await api.post('/orders', payload);
    return response.data;
  },

  /**
   * Duplicate an existing order N times (V1 dupepro/dupeprolist/dupeprosave —
   * save first, flags modal, per-new-pro grid, one save). The backend runs one
   * transaction per new pro: source re-verification + the created-today ACL
   * gate, then prologue/engine/children/EDI per row, so a partial batch is
   * possible — `created` lists the pros that landed and `failed` the rows that
   * didn't.
   *
   * `rows` is one entry per new pro and `dupecount` must equal `rows.length`.
   * With `explicitValues: true` (the WYSIWYG grid — it pre-fills every cell)
   * a blank field lands BLANK; without it, blank inherits the source (except
   * container / lastfreedaydate). `submitNonce`/`bypassDupeCheck` follow the same
   * idempotency + dupe-bypass convention as `createOrder`: a possible-duplicate
   * hit comes back `requiresConfirmation`/`dupes` (nothing written — resend with
   * `bypassDupeCheck: true`), and a nonce replay comes back `duplicate`. Both of
   * those are HTTP 200 and success is 201, so axios resolves either way and
   * callers just branch on the body.
   */
  dupeOrder: async (
    pronumber: number,
    payload: {
      dupecount: number; // 1-199; must equal rows.length
      samemaster: 'Y' | 'N';
      copymrbl: 'Y' | 'N';
      copynotes: 'Y' | 'N';
      copyaccessorials: 'Y' | 'N';
      copydocuments: 'Y' | 'N';
      rows: DupeOrderRow[]; // one per new pro
      submitNonce: string; // idempotency, same convention as createOrder
      bypassDupeCheck?: boolean; // set on a "Duplicate anyway" retry
      explicitValues?: boolean; // true = '' means blank; absent/false = '' inherits source
    },
  ): Promise<{
    success: boolean;
    created?: number[];
    failed?: Array<{ row: number; error: string }>;
    requiresConfirmation?: boolean;
    reason?: string;
    dupes?: Array<{ row: number; pros: Array<string | number> }>;
    duplicate?: boolean;
    message?: string;
    userMessages?: Array<{ text: string; level: 'error' | 'warning' | 'info' }>;
  }> => {
    const response = await api.post(`/orders/${pronumber}/dupe`, payload);
    return response.data;
  },

  /**
   * Save the /pro drawer. Two responses mean NOTHING WAS WRITTEN and the caller must
   * keep the user's pending edits rather than running its post-save reset:
   *   - `requiresConfirmation` + `dupes` — possible duplicate; confirm and resend with
   *     `bypassDupeCheck`.
   *   - `conflict` + `conflicts` (`reason: 'legConflict'`) — another user moved a leg
   *     while this one was editing. `conflicts` is [{legrecno, legnum, field, theirs,
   *     mine}], the same shape the pre-save stale check produces, so both feed one
   *     ConflictMap. `__new__`/`__removed__` in `field` mean a whole leg was added or
   *     deleted by someone else.
   * Both come back 200 with `success: false` — a 4xx would land in the axios error
   * branch, which sets saveError and never reads the body.
   */
  saveOrderDetails: async (payload: {
    pronumber: number;
    originateleg: number;
    deliveryleg: number;
    terminateleg: number;
    legs: any[];
    orderData?: any[];
    // Native-engine invoice section: main-invoice header field edits
    // ([{column,newValue,oldValue}]). Ignored in cmsapi/shadow mode.
    invoiceData?: any[];
    // Native-engine invli section: authoritative main-invoice line-item set
    // ([{recno,new,old}]). Ignored in cmsapi/shadow mode.
    invliData?: any[];
    notes?: string;
    // Reason entered in the Addl Info delete modal; forwarded to the CMS API so
    // sr_orderdelete can note/history/email it. Only sent on a delete.
    deletereason?: string;
    // Transient prompt inputs (V1 $qpst->param): values a modal collected that
    // aren't persisted order/leg columns. The saverules engine reads them via
    // ctx.input(), and the backend forwards the known keys to the CMS API so the
    // Perl rules see them too. Currently the rolling modal's rollingreason /
    // rollingissue / missedcutoffyesno.
    inputs?: Record<string, string>;
    // Native-cutover (SAVERULES_SAVE_MODE='native') idempotency + dupe-bypass.
    // Ignored by cmsapi/shadow mode. submitNonce dedupes a double-submit;
    // bypassDupeCheck is set on a "Save anyway" retry after a duplicate confirm.
    submitNonce?: string;
    bypassDupeCheck?: boolean;
    // Admin Override Mode (#33501): the user turned the per-pro override on and edited a
    // field this record's STATE had locked. The server re-resolves the caller's
    // pro-admin-override grant and 403s a claim without it, so sending this is a request,
    // not an authorization. Its effect is the cms.history tag — rules run either way.
    override?: boolean;
  }): Promise<any> => {
    const response = await api.post('/orders/save-order-details', payload);
    return response.data;
  },

  /** Full detail for one leg document (View side of the V1 legdocumentinfo modal). */
  getLegDocument: async (recno: number): Promise<LegDocumentDetail> => {
    const response = await api.get<LegDocumentDetail>(`/orders/leg-documents/${recno}`);
    return response.data;
  },

  /** Blob for one leg-document file (attachment / POD signature / PDF), streamed from blob
   *  storage. preview=true => inline, false => attachment download. */
  getLegDocumentFileBlob: async (
    recno: number,
    fileRecno: number,
    preview = true,
  ): Promise<Blob> => {
    const response = await api.get<Blob>(
      `/orders/leg-documents/${recno}/files/${fileRecno}`,
      { params: { preview }, responseType: 'blob' },
    );
    return response.data;
  },

  /** Active driver-paperwork rejection reasons for the Reject Paperwork dropdown. */
  getPaperworkRejectionReasons: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/cms-lookups/driver-paperwork-rejection-reasons');
    return response.data;
  },

  /** Reject a leg's paperwork (persists immediately, like Clear Leg). Flips the leg's
   *  require flag back to Y + clears the doc recno, and runs the driverpay/invoice
   *  side-effects server-side. Caller should refresh the pro afterward. */
  rejectLegDocument: async (
    recno: number,
    payload: { reason: string; comments: string },
  ): Promise<{ status: string; recno: number; requireField: string; legnum: number | null }> => {
    const response = await api.post(`/orders/leg-documents/${recno}/reject`, payload);
    return response.data;
  },

  /**
   * Manually cancel a POHA/Navis pregate appointment (v1 "cancelpregate" button).
   * Only HOU-BPT / HOU-BCT; the server prod-gates the real SOAP call (skipped +
   * logged in non-prod). `pregatetype: 'empty'` targets the empty-pregate pin.
   *
   * Waits for the terminal. `verdict` is its answer in v1's wording ("Cancelled
   * Transaction ID 123", "Already Used/Closed", "Error: …") and is what the user
   * is shown. `ok` means the appointment is gone; `cleared` means the server also
   * blanked the PIN + chassis on the order, so the caller should re-fetch.
   */
  cancelPregate: async (payload: {
    pronumber: number | string;
    rail: string;
    pregatepin: string;
    pregatetype?: 'empty';
  }): Promise<{ ok: boolean; skipped: boolean; cleared?: boolean; verdict?: string }> => {
    const response = await api.post('/orders/cancel-pregate', payload);
    return response.data;
  },

  /** Save the per-diem responsibility note (v1 saveperdieminfo). Upserts
   *  orders_perdiemresponsible.perdiemnotes, logs a cms.history row for the column,
   *  and drops a PER DIEM APPROVAL note. `changed` is false when the note came back
   *  identical — v1 writes nothing at all in that case. */
  savePerDiemNotes: async (
    pronumber: number | string,
    perDiemNotes: string,
  ): Promise<{ ok: boolean; perDiemNotes: string; changed: boolean }> => {
    const response = await api.post('/orders/perdiem-notes', { pronumber, perDiemNotes });
    return response.data;
  },

  /**
   * Persist editable invoice-header fields (ordersall + invoice split handled
   * server-side). Called by the order-level save (useOrderEditing.save) right
   * after the main order save, so the whole Invoicing tab commits on one Save.
   */
  saveInvoiceHeader: async (
    pronumber: number | string,
    patch: InvoiceHeaderPatch,
  ): Promise<{ success: boolean }> => {
    const response = await api.post(`/orders/${pronumber}/invoice-header`, patch);
    return response.data;
  },

  /**
   * Whether an order/template may be marked deleted (legacy fv_deleted). Called
   * before prompting for a delete reason. Templates are always allowed.
   * Returns { allowed, reason } — `reason` lists why not (e.g. "Invoiced").
   */
  getDeleteEligibility: async (
    pronumber: number | string,
  ): Promise<{ allowed: boolean; reason: string }> => {
    const response = await api.get(`/orders/${pronumber}/delete-eligibility`);
    return response.data;
  },

  /**
   * Initiate a street turn (reuse a container from `pronumber` onto `reusepro`).
   * 422 returns a `detail.errors` array of human-readable validation messages.
   */
  reuseContainer: async (pronumber: number, reusepro: number): Promise<any> => {
    const response = await api.post(`/streetturn/reuse/${pronumber}`, { reusepro });
    return response.data;
  },

  /**
   * Undo a previously-applied reuse. `pronumber` is the export side (the pro
   * whose `reusecontainerpro` points back at the import).
   */
  undoReuseContainer: async (pronumber: number): Promise<any> => {
    const response = await api.post(`/streetturn/undo-reuse/${pronumber}`, {});
    return response.data;
  },

  /**
   * Send the rail bill for an order. `loadedEmpty` is 'EMPTY' or 'LOADED'.
   * Returns `{ success, message, emailSent }`.
   */
  sendRailBilling: async (
    pronumber: number,
    loadedEmpty: 'EMPTY' | 'LOADED',
  ): Promise<{ success: boolean; message: string; emailSent: boolean }> => {
    const response = await api.post(`/orders/${pronumber}/rail-billing/send`, { loadedEmpty });
    return response.data;
  },

  /**
   * Cancel a previously-sent rail bill. Returns `{ success, message, emailSent }`.
   */
  cancelRailBilling: async (
    pronumber: number,
  ): Promise<{ success: boolean; message: string; emailSent: boolean }> => {
    const response = await api.post(`/orders/${pronumber}/rail-billing/cancel`, {});
    return response.data;
  },

  /**
   * Get list of active chassis provider abbreviations for dropdown selection
   */
  getChassisProviders: async (signal?: AbortSignal): Promise<string[]> => {
    // Reference list, fetched app-level AND per EdiCreateModal open — shared cache.
    const response = await cachedGet<string[]>('/orders/chassis-providers', { signal });
    return response.data;
  },

  /**
   * Get list of active chassis from equipment list for chassis ID selection
   */
  getChassisEquipment: async (signal?: AbortSignal): Promise<ChassisEquipment[]> => {
    const response = await api.get('/orders/chassis-equipment', { signal });
    return response.data;
  },

  /**
   * Get list of active vans from equipment list for container selection on
   * GWI-equipment VAN orders. Parallel to getChassisEquipment.
   */
  getVanEquipment: async (signal?: AbortSignal): Promise<VanEquipment[]> => {
    const response = await api.get('/orders/van-equipment', { signal });
    return response.data;
  },

  /**
   * Send selected orders to gwiMove
   */
  sendToGwiMove: async (items: SendToGwiMoveItem[]): Promise<SendToGwiMoveResponse> => {
    const response = await api.post('/orders/send-to-gwimove', { items });
    return response.data;
  },

  /**
   * Remove selected moves from their assigned drivers
   */
  removeFromDriver: async (items: RemoveFromDriverItem[]): Promise<RemoveFromDriverResponse> => {
    const response = await api.post('/orders/remove-from-driver', { items });
    return response.data;
  },

  checkDeadlineCriteria: async (items: CheckDeadlineItem[]): Promise<CheckDeadlineResponse> => {
    const response = await api.post('/orders/check-deadline-criteria', { items });
    return response.data;
  },

  /**
   * Get distinct filter options for size, container type, and chassis type
   */
  getFilterOptions: async (): Promise<{ sizes: string[]; chassisTypes: string[]; containerTypes: string[] }> => {
    const response = await api.get('/orders/filter-options');
    return response.data;
  },

  // Get notes for an order
  getNotes: async (pronumber: number | string): Promise<{ notes: { recno: number; datetime: string; userid: string; notes: string }[] }> => {
    const response = await api.get(`/orders/${pronumber}/notes`);
    return response.data;
  },

  // Add a note to an order
  addNote: async (pronumber: number | string, notes: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/orders/${pronumber}/notes`, { notes });
    return response.data;
  },

};

export default ordersService;
